import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isEqual from 'lodash/isEqual';
import orderBy from 'lodash/orderBy';
import isBoolean from 'lodash/isBoolean';
import styled from 'styled-components';
import Checkbox from 'material-ui/Checkbox';
import CustomDialog from '../custom_dialog';
import Loading from '../../../../components/Loading';
import Colors from '../../../../styles/colors';
import ModalDialog, {
  CloseButton,
  InfoButton,
  SaveButton,
} from '../../../../components/ModalDialog';
import { soundcastManager } from '../../../../helpers/managers';

class PendingInvitesModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      sending: false,
      removing: false,
      invitees: [],
      selectAll: false,
      selectedInvitees: [],
      error: '',
    };

    this.onSelectAll = this.onSelectAll.bind(this);
    this.onSelectInvitee = this.onSelectInvitee.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.removeInvite = this.removeInvite.bind(this);
    this.sendInviteReminder = this.sendInviteReminder.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.soundcast, nextProps.soundcast)) {
      this.assignInvitess(nextProps);
    }
  }

  componentDidMount() {
    this.assignInvitess();
  }

  assignInvitess(props = {}) {
    props = !isEmpty(props) ? props : this.props;
    this.parseSoundcastInvitees(props.soundcast);
  }

  parseSoundcastInvitees(soundcast) {
    return new Promise(resolve => {
      const { invited = {} } = soundcast;
      const restoreDots = x => x.replace(/\(dot\)/g, '.');
      const invitees = Object.entries(invited)
        .filter(([, value]) => value !== false && value !== null)
        .map(([email, invitation]) => {
          const invited = isObject(invitation) ? invitation.date_subscribed : invitation;
          const paid = isObject(invitation) ? !isEmpty(invitation.paymentID) : false;
          return {
            email: restoreDots(email),
            invited,
            paid,
          };
        });

      this.setState({ invitees: orderBy(invitees, ['invited'], ['desc']) }, resolve);
    });
  }

  closeModal() {
    this.props.onClose();
  }

  removeInvite() {
    const { soundcast, onRemoveInvites } = this.props;
    const { selectedInvitees, invitees } = this.state;

    this.setState({ removing: true }, async () => {
      try {
        if (isEmpty(soundcast.id)) {
          throw new Error('Soundcast ID cannot be empty.');
        }

        const removedInvitees = selectedInvitees.map(invitee => invitee.email);
        await soundcastManager.removeInvitedUserByEmailBulk(soundcast.id, removedInvitees);
        await new Promise(resolve => setTimeout(resolve, 1000));

        onRemoveInvites({ soundcast, removedInvitees });

        // remove from invitees state
        const ninvitees = invitees.filter(invitee => !removedInvitees.includes(invitee.email));
        this.setState({ invitees: ninvitees, selectAll: false, selectedInvitees: [] }, () => {
          this.alertDialog.showModal({
            content: 'Pending invites have been succesfully removed.',
          });
        });
      } catch (err) {
        console.log(err);
        this.alertDialog.showModal({
          content:
            'There was a problem removing invitation, ' +
            'please contact support at support@mysoundwise.com if the issue persists.' +
            ` ${err.message}`,
        });
      } finally {
        this.setState({ removing: false });
      }
    });
  }

  sendInviteReminder() {
    const { soundcast } = this.props;
    const {invitees, selectedInvitees } = this.state;

    this.setState({ sending: true }, async () => {
      try {
        const body = {
          soundcastId: soundcast.id,
        };

        // if not all invitees selected, perform a selective send
        if (!isEqual(invitees.length, selectedInvitees.length)) {
          body.invitees = selectedInvitees.map(invitee => invitee.email);
        }

        // send the invitation reminder
        await Axios.post('/api/invitation_reminder', body);

        // pull latest soundcast
        const nsoundcast = await soundcastManager.getById(soundcast.id);
        await this.parseSoundcastInvitees(nsoundcast);
        this.setState(
          {
            sending: false,
            selectAll: false,
            selectedInvitees: [],
            error: '',
          },
          () => {
            this.alertDialog.showModal({
              content: 'Invitation reminder has been sent.',
              onClose: this.closeModal,
            });
          }
        );
      } catch (err) {
        console.log(err);
        this.alertDialog.showModal({
          content:
            'There was a problem sending invitation reminder, please contact support at support@mysoundwise.com if the issue persists.',
        });
      } finally {
        this.setState({ sending: false });
      }
    });
  }

  onSelectAll(_e, isChecked) {
    const { invitees } = this.state;
    const selectedInvitees = isChecked ? [...invitees.filter(item => !item.paid)] : [];
    this.setState({
      selectAll: isChecked,
      selectedInvitees,
    });
  }

  onSelectInvitee(invitee) {
    return (_e, isChecked) => {
      let { invitees, selectedInvitees } = this.state;
      if (isChecked) {
        selectedInvitees.push({ ...invitee });
      } else {
        // look for the already selectedInvitees index and remove it
        selectedInvitees = selectedInvitees.filter(sinvitee => !isEqual(sinvitee, invitee));
      }

      const selectAll = isEqual(invitees.length, selectedInvitees.length);
      this.setState({
        selectAll,
        selectedInvitees,
      });
    };
  }

  isInviteeSelected(invitee) {
    const { selectedInvitees } = this.state;

    if (isEmpty(invitee)) {
      return false;
    }

    const exist = selectedInvitees.find(sinvitee => isEqual(sinvitee, invitee));
    return exist ? true : false;
  }

  isAllInviteesSelected() {
    const { invitees, selectedInvitees } = this.state;
    return isEqual(invitees.length, selectedInvitees.length);
  }

  renderPendingList() {
    const { invitees } = this.state;
    if (isEmpty(invitees)) {
      return <PendingListEmpty>No Pending Invites</PendingListEmpty>;
    }

    return invitees.map((invitee, i) => {
      const paidTooltipTitle = 'This invitation was automatically generated ' +
        'when a non-registered user was purchased content and cannot be selected.';
      return (
        <ListRow className="list-row" key={i}>
          <div className="list-item">
            {invitee.paid
            ? <div style={{cursor: 'default'}} datatoggle="tooltip" dataplacement="top" title={paidTooltipTitle}>
                <span>
                  <i className="material-icons paid-icon">paid</i>
                </span>
                <span>
                  {invitee.email}
                </span>
              </div> 
            : <Checkbox
                checked={this.isInviteeSelected(invitee)}
                label={invitee.email}
                onCheck={this.onSelectInvitee(invitee)}
                style={{
                  padding: '5px 0',
                }}
                labelStyle={{
                  width: '100%',
                  fontSize: '1.6rem',
                  fontWeight: 'normal',
                  lineHeight: '28px',
                  margin: 0,
                }}
                iconStyle={{
                  width: 25,
                  height: 25,
                  fill: '#ff8f53',
                  transition: 'none',
                }}
              />
            }
          </div>
          <div className="list-item xs-display-none">
            {!isBoolean(invitee.invited)
              ? moment.unix(invitee.invited).format('MMM DD, YYYY')
              : '-'}
          </div>
        </ListRow>
      );
    });
  }

  render() {
    const { isShown, soundcast } = this.props;
    const { selectAll, sending, removing, invitees, selectedInvitees, error } = this.state;
    const sendDisabled = isEmpty(selectedInvitees) || sending;
    const removeDisabled = sendDisabled || removing;

    return (
      <React.Fragment>
        <ModalDialog
          title={`Pending Invite List for ${soundcast.title}`}
          open={isShown}
          actions={[
            <CloseButton
              className="btn text-medium margin-two-right naked"
              onClick={this.closeModal}
              disabled={sending}>
              Close
            </CloseButton>,
            <InfoButton
              className="btn text-medium margin-two-right"
              onClick={this.removeInvite}
              disabled={removeDisabled}>
              {removing && (
                <Loading
                  text="REMOVING"
                  type="circle"
                  align="left"
                  size={20}
                  fontSize={1.4}
                  fill="#fff"
                  color="#fff"
                  textStyle={{ margin: '0 5px' }}
                />
              )}
              {!removing && 'Remove'}
            </InfoButton>,
            <SaveButton
              className="btn text-medium"
              onClick={this.sendInviteReminder}
              disabled={sendDisabled}>
              {sending && (
                <Loading
                  text="SENDING"
                  type="circle"
                  align="left"
                  size={20}
                  fontSize={1.4}
                  fill="#fff"
                  color="#fff"
                  textStyle={{ margin: '0 5px' }}
                />
              )}
              {!sending && 'Send Reminder'}
            </SaveButton>,
          ]}
          modal={false}
          bodyStyle={{ overflowY: 'auto', marginTop: 43 }}>
          {!isEmpty(error) && (
            <div className="row">
              <div className="col-lg-12 col-md-12 col-xs-12">
                <div className="alert alert-danger text-medium" role="alert">
                  {error}
                </div>
              </div>
            </div>
          )}
          <StickyRow className="list-row">
            <div className="list-item">
              <Checkbox
                checked={selectAll}
                disabled={isEmpty(invitees)}
                label="EMAIL"
                onCheck={this.onSelectAll}
                labelStyle={{
                  fontSize: '1.6rem',
                  lineHeight: '26px',
                  margin: 0,
                }}
                iconStyle={{
                  width: 25,
                  height: 25,
                  fill: '#ff8f53',
                  transition: 'none',
                }}
              />
            </div>
            <div className="list-item xs-display-none">
              <span className="text-large font-weight-600">INVITED ON</span>
            </div>
          </StickyRow>
          <PendingListBody>{this.renderPendingList()}</PendingListBody>
        </ModalDialog>
        <CustomDialog ref={ref => (this.alertDialog = ref)} />
      </React.Fragment>
    );
  }
}

const ListRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${Colors.lightBorder};

  .paid-icon {
    color: #ff8f53;
    cursor: default;
    padding-right: 0.75em;
  }
  .list-item {
    color: ${Colors.fontBlack};
    padding: 8px 10px;
  }
`;

const StickyRow = styled(ListRow)`
  position: fixed;
  width: 100%;
  right: 0;
  padding: 0px 24px 0px;
  background: #ffffff;
  z-index: 5;
  margin-top: -43px;
`;

const PendingListBody = styled.div`
  min-height: 120px;

  .list-row {
    &:last-child {
      border-bottom: none;
    }
  }
`;

const PendingListEmpty = styled.div`
  display: flex;
  height: 120px;
  align-items: center;
  justify-content: center;
`;

PendingInvitesModal.propTypes = {
  isShown: PropTypes.bool,
  soundcast: PropTypes.object,
  userInfo: PropTypes.object,
  onClose: PropTypes.func,
  onRemoveInvites: PropTypes.func,
};

PendingInvitesModal.defaultProps = {
  isShown: false,
  soundcast: {},
  userInfo: {},
  onClose: () => {},
  onRemoveInvites: () => {},
};

export default PendingInvitesModal;
